import React, { useContext } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import styles from "./AiRealestateHint.module.css";

interface AiRealestateHintProps {
    /**
     * When true, renders identical markup with visibility: hidden so a column
     * next to the primary hint can grow in lockstep — used to keep the
     * Objekt (Liegenschaft) and Auftrag (Ticket) columns vertically aligned
     * when the Liegenschaft field emits a hint underneath it.
     */
    mirrorOnly?: boolean;
}

const AiRealestateHintBase = ({ mirrorOnly = false }: AiRealestateHintProps) => {
    const { t } = useTranslation();
    const { invoiceStore, invoiceFilePreviewStore } = useContext(RootStoreContext);

    const aiDetection = invoiceStore.currentInvoice?.aiDetection;
    const outcome = aiDetection?.results.realestateid?.outcome;

    const noRealestatePicked = !invoiceStore.currentInvoice?.realestate?.id;
    const notUniqueVisible = !!aiDetection && outcome === "ambiguous" && noRealestatePicked;
    const notFoundVisible =
        !invoiceFilePreviewStore.isAiPending &&
        (outcome === "no_query" || outcome === "no_match") &&
        noRealestatePicked;

    // Outcomes are mutually exclusive by definition — a single "message" wins.
    let message: string | undefined;
    if (notUniqueVisible) {
        message = t("screens.kredi_flow.ai.realestate_not_unique");
    } else if (notFoundVisible) {
        message = t("screens.kredi_flow.ai.realestate_not_found");
    }

    if (!message) {
        return null;
    }

    // When the Liegenschaft field is required-but-empty, Bootstrap renders the
    // .invalid-feedback text right below the input in the same row space the AI
    // hint's negative top-margin was pulled into. Push the hint further down in
    // that case so the two messages stack cleanly. Both the visible hint and the
    // mirror observe this same flag, so the columns stay aligned.
    const hasValidationError = !!invoiceStore.currentInvoice?.errors.realestateRequired;

    return (
        <div
            className={`${styles.AiRealestateHint} ${
                hasValidationError ? styles.AiRealestateHintWithValidationError : ""
            }`}
            aria-hidden={mirrorOnly}
            style={mirrorOnly ? { visibility: "hidden" } : undefined}
        >
            <UI.Icon icon={UI.SVGIcon.InformationFilled} size={16} color="#C26A00" />
            <span>{message}</span>
        </div>
    );
};

export const AiRealestateHint = observer(AiRealestateHintBase);
